'use client';

import { PopoverArrow } from '@radix-ui/react-popover';
import Link from 'next/link';
import { type MouseEvent, useState } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/UI/Popover';
import { cn } from '@/utils/ui';

import type { AccountNavigationLink } from './HeaderAccountNav';
import { NavTopCaret } from './NavTopCaret';

interface AccountNavPopoverProps {
  accountNavigationLinks: AccountNavigationLink[];
  onNavigationClick: (event: MouseEvent, navItem: AccountNavigationLink) => void;
  contrastTextColor: string;
  variant: 'mobile' | 'desktop';
  className?: string;
}

export function AccountNavPopover({
  accountNavigationLinks,
  onNavigationClick,
  contrastTextColor,
  variant,
  className,
}: AccountNavPopoverProps): React.ReactNode {
  const [isOpen, setIsOpen] = useState(false);

  const accountIconClasses = `
    flex
    size-[30px]
    items-center
    justify-center
    cursor-pointer
    relative
    bg-transparent
    after:content-[''] after:block after:absolute after:p-3 after:md:p-5
  `;

  const isMobile = variant === 'mobile';

  const handleClick = (): void => {
    setIsOpen(!isOpen);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className={cn(
          accountIconClasses,
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
          className,
        )}
        onClick={handleClick}
      >
        <Icon className="size-[30px]" color={contrastTextColor} id="account" />
      </PopoverTrigger>

      <PopoverContent
        align="end"
        alignOffset={isMobile ? undefined : -25}
        sideOffset={isMobile ? undefined : 10}
        className={cn(
          'mt-2 rounded-2xl border border-base-white/10 bg-base-primary/60 shadow-[0px_4px_24px_-2px_black/65] backdrop-blur-[50px] focus:outline-none focus-visible:outline-none',
          isMobile ? 'sm:hidden' : 'hidden sm:flex',
        )}
        style={{ WebkitBackdropFilter: 'blur(50px)' }}
      >
        <PopoverArrow asChild>
          <NavTopCaret />
        </PopoverArrow>

        <ul>
          {accountNavigationLinks.map((navLink) => {
            const dataAnalyticsStr = navLink.title.toLowerCase().replace(/ /g, '');
            const analyticsId = isMobile
              ? 'header-account-mobile-navigation-link'
              : `header-account-nav-${dataAnalyticsStr}-button`;

            return (
              <li key={navLink.title}>
                <Link
                  data-analytics={analyticsId}
                  className={cn(
                    'flex items-center justify-start px-6 py-4 font-FoxOneAeroBold text-[13px]/[18px] uppercase text-white/70 hover:bg-white/10',
                    isMobile ? 'w-full active:bg-white/10' : 'h-[50px] focus:bg-white/10',
                  )}
                  href={navLink.href ?? '/'}
                  onClick={(event: MouseEvent) => {
                    onNavigationClick(event, navLink);
                  }}
                >
                  {navLink.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
